import api from '@/lib/api';

// ─── Query interfaces ────────────────────────────────────────────────────────

export interface InventoryQuery {
  propertyId: string;
  from: string;
  to: string;
}

export interface RatesQuery {
  propertyId: string;
  from: string;
  to: string;
}

// ─── Raw API response (what GET /availability/inventory actually returns) ─────
// Shape: { unitTypes: [{ id, name, dates: [{ date, total, available, sold, outOfOrder }] }] }

export interface ApiDateInventory {
  date: string;
  total: number;
  available: number;
  sold: number;
  outOfOrder: number;
}

export interface ApiUnitTypeInventory {
  id: string;
  name: string;
  dates: ApiDateInventory[];
}

export interface ApiInventoryResponse {
  unitTypes: ApiUnitTypeInventory[];
}

// ─── Grid response interfaces (shape consumed by AvailabilityGridPage) ────────

export interface InventoryCell {
  date: string;
  totalRooms: number;
  available: number;
  sold: number;
  outOfOrder: number;
}

export interface RoomTypeRow {
  roomTypeId: string;
  roomTypeName: string;
  cells: InventoryCell[];
}

export interface SummaryRow {
  cells: InventoryCell[];
}

export interface InventoryGridResponse {
  dates: string[];
  roomTypes: RoomTypeRow[];
  summary: SummaryRow;
}

/**
 * Adapt the backend `{ unitTypes[].dates[] }` contract to the grid's
 * `{ dates[], roomTypes[].cells[], summary }` shape. The backend never sends
 * `roomTypes`/`cells`/`totalRooms`/`summary` — this bridges the gap so the grid
 * actually renders (previously it read `roomTypes` off the raw response, which
 * is always undefined → permanent empty state).
 */
export function mapInventoryToGrid(api: ApiInventoryResponse | undefined): InventoryGridResponse {
  const unitTypes = api?.unitTypes ?? [];

  // Union of all dates across unit types, chronologically ordered.
  const dateSet = new Set<string>();
  for (const ut of unitTypes) {
    for (const d of ut.dates ?? []) dateSet.add(d.date);
  }
  const dates = Array.from(dateSet).sort();

  const roomTypes: RoomTypeRow[] = unitTypes.map((ut) => {
    const byDate = new Map((ut.dates ?? []).map((d) => [d.date, d]));
    return {
      roomTypeId: ut.id,
      roomTypeName: ut.name,
      cells: dates.map((date) => {
        const d = byDate.get(date);
        return {
          date,
          totalRooms: d?.total ?? 0,
          available: d?.available ?? 0,
          sold: d?.sold ?? 0,
          outOfOrder: d?.outOfOrder ?? 0,
        };
      }),
    };
  });

  // Column totals across every room type.
  const summaryCells: InventoryCell[] = dates.map((date) => {
    const acc = { date, totalRooms: 0, available: 0, sold: 0, outOfOrder: 0 };
    for (const rt of roomTypes) {
      const cell = rt.cells.find((c) => c.date === date);
      if (cell) {
        acc.totalRooms += cell.totalRooms;
        acc.available += cell.available;
        acc.sold += cell.sold;
        acc.outOfOrder += cell.outOfOrder;
      }
    }
    return acc;
  });

  return { dates, roomTypes, summary: { cells: summaryCells } };
}

export interface RatePlanRate {
  ratePlanId: string;
  ratePlanCode: string;
  ratePlanName: string;
  nightlyRate: string;
  currency: string;
}

export interface AvailabilityCard {
  roomTypeId: string;
  roomTypeName: string;
  roomTypeCode: string;
  maxOccupancy: number;
  availableCount: number;
  isSoldOut: boolean;
  ratePlans: RatePlanRate[];
  cancellationPolicyName: string | null;
}

export interface AvailabilityRatesResponse {
  roomTypes: AvailabilityCard[];
}

// ─── API functions ───────────────────────────────────────────────────────────

export async function fetchInventory(query: InventoryQuery): Promise<InventoryGridResponse> {
  const { data } = await api.get<ApiInventoryResponse>('/availability/inventory', { params: query });
  return mapInventoryToGrid(data);
}

export async function fetchRates(query: RatesQuery): Promise<AvailabilityRatesResponse> {
  const { data } = await api.get('/availability/rates', { params: query });
  return data;
}
